import { type ReactElement, useMemo } from 'react';
import { s } from '@/theme/tokens';
import { Box, Stack, Text } from '@/ui';
import { AddIcon, Button, HelpOutline, IconButton, Tooltip } from '@/ui/primitives';
import { EditableTagRow } from './EditableTagRow';
import { defaultTagColor } from './EditableTagsSection.utils';
import type { TagColor, TagOption } from '@/shared/data/tags';

type EditableTagsSectionProps = {
  tags: string[];
  colors: TagColor[];
  tagOptions?: TagOption[];
  onTagsChange: (value: string[]) => void;
  onColorsChange: (value: TagColor[]) => void;
  label?: string;
  helperText?: string;
};

export const EditableTagsSection = ({
  tags,
  colors,
  tagOptions = [],
  onTagsChange,
  onColorsChange,
  label = 'Tags',
  helperText,
}: EditableTagsSectionProps): ReactElement => {
  const tagNameOptions = useMemo(() => tagOptions.map((option) => option.value), [tagOptions]);

  const setTagValueAtIndex = (index: number, nextValue: string): void => {
    onTagsChange(tags.map((item, itemIndex) => (itemIndex === index ? nextValue : item)));
    const matchingOption = tagOptions.find((option) => option.value === nextValue.trim());
    if (!matchingOption || colors[index] === matchingOption.color) {
      return;
    }
    onColorsChange(
      colors.map((item, itemIndex) => (itemIndex === index ? matchingOption.color : item)),
    );
  };

  return (
    <Stack gap="none" sx={{ width: '100%', mt: s('none') }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
        <Text
          role="body"
          sx={(theme) => ({
            fontSize: theme.typography.subtitle1.fontSize,
            lineHeight: theme.typography.subtitle1.lineHeight,
          })}
        >
          {label}
        </Text>
        {helperText ? (
          <Tooltip
            title={
              <Text
                role="body"
                sx={(theme) => ({
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                })}
              >
                {helperText}
              </Text>
            }
            placement="top"
            slotProps={{
              tooltip: {
                sx: (theme) => ({
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                  maxWidth: 440,
                }),
              },
            }}
          >
            <IconButton
              size="small"
              aria-label={`More information about ${label.toLowerCase()}`}
              sx={{
                p: s('none'),
                color: 'text.secondary',
              }}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Stack gap="md" sx={{ mt: tags.length > 0 ? s('sm') : s('xs') }}>
        {tags.map((tag, index) => (
          <EditableTagRow
            key={`editable-tag-${index}`}
            tag={tag}
            color={colors[index]}
            index={index}
            tagNameOptions={tagNameOptions}
            onTagChange={(nextValue) => {
              setTagValueAtIndex(index, nextValue);
            }}
            onColorChange={(nextValue) => {
              onColorsChange(
                colors.map((item, itemIndex) => (itemIndex === index ? nextValue : item)),
              );
            }}
            onRemove={() => {
              onTagsChange(tags.filter((_item, itemIndex) => itemIndex !== index));
              onColorsChange(colors.filter((_item, itemIndex) => itemIndex !== index));
            }}
          />
        ))}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: s('md') }}>
        <Button
          variant="text"
          startIcon={<AddIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
          onClick={() => {
            onTagsChange([...tags, '']);
            onColorsChange([...colors, defaultTagColor]);
          }}
          sx={{
            pl: s('none'),
            minWidth: 0,
            '& .MuiButton-startIcon': {
              marginLeft: s('none'),
              marginRight: s('xxxs'),
            },
          }}
        >
          Add Tag
        </Button>
      </Box>
    </Stack>
  );
};
